import React from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Trash2, Calendar, ArrowRight, BookmarkX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { useBookmarks } from '../contexts/BookmarksContext';
import LazyImage from '../components/LazyImage';

const Bookmarks = () => {
  const { bookmarks, removeBookmark, clearAllBookmarks } = useBookmarks();

  return (
    <div className="min-h-screen bg-gray-950">
      <SEO 
        title="Saved Articles"
        description="Your bookmarked articles on TruckAccidentSupport.com"
        canonical="/bookmarks"
      />

      {/* Header */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10">
                  <Bookmark className="h-6 w-6 text-orange-400" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white">
                  Saved Articles
                </h1>
              </div>
              <p className="text-gray-400">
                {bookmarks.length === 0 
                  ? "You haven't saved any articles yet"
                  : `${bookmarks.length} article${bookmarks.length !== 1 ? 's' : ''} saved`
                }
              </p>
            </div>
            
            {bookmarks.length > 0 && (
              <Button 
                variant="outline" 
                onClick={clearAllBookmarks}
                className="border-gray-700 text-gray-300 hover:text-red-400 hover:border-red-500"
                data-testid="clear-bookmarks-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Bookmarks List */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {bookmarks.length === 0 ? (
            <div className="text-center py-16">
              <BookmarkX className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">No saved articles</h2>
              <p className="text-gray-400 mb-6">
                Start bookmarking articles to read them later
              </p>
              <Link to="/blog">
                <Button className="btn-primary">
                  Browse Articles
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookmarks.map((post) => (
                <Card key={post.id} className="card-hover bg-gray-800 border-gray-700 overflow-hidden group" data-testid={`bookmarked-post-${post.id}`}>
                  <Link to={`/blog/${post.id}`}>
                    <div className="aspect-video overflow-hidden">
                      <LazyImage 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span className="badge badge-orange">{post.category}</span>
                      <div className="flex items-center text-xs text-gray-400">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(post.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>
                    <Link to={`/blog/${post.id}`}>
                      <CardTitle className="text-white text-xl hover:text-orange-400 transition-colors">
                        {post.title}
                      </CardTitle>
                    </Link>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400 mb-4 line-clamp-2">
                      {post.excerpt}
                    </CardDescription>
                    <div className="flex items-center justify-between">
                      <Link to={`/blog/${post.id}`}>
                        <Button variant="link" className="text-orange-400 hover:text-orange-300 p-0">
                          Read Article
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeBookmark(post.id)}
                        className="text-gray-400 hover:text-red-400"
                        data-testid={`remove-bookmark-${post.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Bookmarks;
